import { Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import { AccountPicker } from "components/holdings/AccountPicker";
import { AccountStrategyPicker } from "components/holdings/AccountStrategyPicker";
import { YesNoSelect } from "components/ui/YesNoSelect";
import dayjs from "dayjs";
import { useAppDispatch, useTrading } from "features/store";
import {
  AccountStrategy,
  useCreateAccountHistoryMutation,
  useDeleteAccountHistoryMutation,
  useGetAccountHistoryQuery,
  useRemoveAccountStrategyMutation,
} from "features/trading/accounts";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { AddAccountStrategyModal } from "components/holdings/AddAccountStrategyModal";
import TrashIcon from "@mui/icons-material/Delete";
import { setSelectedAccountStrategy } from "features/trading/tradingSlice";
import { AdjustmentRow, AdjustmentTypeEnum } from "helpers/types";
import { getAdjustmentTypeColor, strategyStyles } from "styles/Strategy.styles";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";

const columns: GridColDef[] = [
  {
    field: "composite",
    headerName: "Composite",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "reporting",
    headerName: "Reporting",
    type: "string",
  },
  {
    field: "reinvest",
    headerName: "Reinvest $",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "keywork",
    headerName: "Keyword",
    type: "boolean",
    renderCell: (params: any) => <YesNoSelect value={params.value} />,
  },
  {
    field: "underlying",
    headerName: "Underlying",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
  {
    field: "management",
    headerName: "Management",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
  {
    field: "advisor__name",
    headerName: "Advisor",
    type: "string",
    valueFormatter: ({ value }: GridValueFormatterParams) => value || "N/A",
  },
].map((col) => ({
  ...col,
  align: "center",
}));

interface CreateAdjustmentFormValues {
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: string;
}

const getDefaultAdjustmentType = (
  hasAccountHistory: boolean,
): AdjustmentTypeEnum =>
  hasAccountHistory ? AdjustmentTypeEnum.BUY : AdjustmentTypeEnum.INCEPTION;

const defaultCreateAdjustmentFormValues: CreateAdjustmentFormValues = {
  adjustment_date: dayjs().format("YYYY-MM-DD"),
  adjustment_type: AdjustmentTypeEnum.BUY,
  adjustment_amount: "",
};

const getAdjustmentColumns = (onRemove: (id: number) => void): GridColDef[] => [
  {
    field: "adjustment_date",
    headerName: "Date",
    type: "string",
    minWidth: 180,
    flex: 1,
    valueFormatter: ({ value }: GridValueFormatterParams) =>
      value ? dayjs(value as string).format("MM/DD/YYYY") : "N/A",
  },
  {
    field: "adjustment_type",
    headerName: "Adjustment Type",
    type: "string",
    minWidth: 180,
    flex: 1,
    renderCell: (params) => {
      const adjustmentType = params.value as AdjustmentTypeEnum;

      return (
        <Typography
          color={getAdjustmentTypeColor(adjustmentType)}
          fontWeight={600}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: "adjustment_amount",
    headerName: "Adjustment Amount",
    type: "string",
    minWidth: 200,
    flex: 1,
  },
  {
    field: "unit_balance",
    headerName: "Unit Balance (At the moment)",
    type: "string",
    minWidth: 260,
    flex: 1.2,
  },
  {
    field: "actions",
    headerName: "Actions",
    minWidth: 120,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<AdjustmentRow>) => (
      <IconButton
        size="small"
        color="error"
        aria-label="Remove adjustment record"
        onClick={(event) => {
          event.stopPropagation();
          onRemove(params.row.id);
        }}
      >
        <TrashIcon fontSize="small" />
      </IconButton>
    ),
  },
];

const DUMMY_COMPOSITE_HISTORY = [
  {
    id: 1,
    as_of_date: "2024-01-14",
    composite: true,
    reporting: "Quarterly",
    reinvest: true,
    keywork: true,
    underlying: "Covered",
    management: "Direct",
    advisor__name: "David Watts",
    notes: "Notes go here, them might be long I'm not sure",
  },
  {
    id: 2,
    as_of_date: "2023-12-15",
    composite: false,
    reporting: "Annual",
    reinvest: true,
    keywork: true,
    underlying: "Uncovered",
    management: "Yes",
    advisor__name: "David Watts",
    notes: "Did not look like a good idea",
  },
  {
    id: 3,
    as_of_date: "2023-11-13",
    composite: true,
    reporting: "Monthly",
    reinvest: true,
    keywork: true,
    underlying: "Covered",
    management: "Direct",
    advisor__name: "David Watts",
    notes: "Nothing to say here",
  },
];

export default function Strategy() {
  const dispatch = useAppDispatch();
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const { selectedAccountStrategy, currentAccountId } = useTrading();
  const [removeAccountStrategy] = useRemoveAccountStrategyMutation();
  const { data: accountHistory = [], isLoading: isAccountHistoryLoading } =
    useGetAccountHistoryQuery(
      {
        account_id: currentAccountId as number,
        strategy_id: selectedAccountStrategy?.strategy as number,
        ordering: "-adjustment_date",
      },
      { skip: !currentAccountId || !selectedAccountStrategy?.strategy },
    );
  const [createAccountHistory, { isLoading: isCreatingAccountHistory }] =
    useCreateAccountHistoryMutation();
  const [deleteAccountHistory, { isLoading: isDeletingAccountHistory }] =
    useDeleteAccountHistoryMutation();
  const [isCreateAdjustmentModalOpen, setIsCreateAdjustmentModalOpen] =
    useState<boolean>(false);
  const [createAdjustmentForm, setCreateAdjustmentForm] =
    useState<CreateAdjustmentFormValues>(defaultCreateAdjustmentFormValues);
  const hasAccountHistory = accountHistory.length > 0;
  const availableAdjustmentTypes = useMemo(
    () =>
      hasAccountHistory
        ? [AdjustmentTypeEnum.BUY, AdjustmentTypeEnum.SELL]
        : [AdjustmentTypeEnum.INCEPTION],
    [hasAccountHistory],
  );

  let rows: Partial<AccountStrategy>[] = [
    ...(selectedAccountStrategy ? [selectedAccountStrategy] : []),
    ...(showHistory ? DUMMY_COMPOSITE_HISTORY : []),
  ];

  const [isOpenedAddModal, setIsOpenAddModal] = useState<boolean>(false);
  const openAddModal = () => setIsOpenAddModal(true);
  const onAddModalClose = () => setIsOpenAddModal(false);
  const onRemoveAccountStrategy = (id?: number) => () => {
    if (id !== undefined) {
      removeAccountStrategy(id);
    }
    dispatch(setSelectedAccountStrategy(undefined));
    setShowHistory(false);
  };

  const openCreateAdjustmentModal = () => {
    const defaultAdjustmentType = getDefaultAdjustmentType(hasAccountHistory);
    setCreateAdjustmentForm({
      ...defaultCreateAdjustmentFormValues,
      adjustment_type: defaultAdjustmentType,
    });
    setIsCreateAdjustmentModalOpen(true);
  };

  const closeCreateAdjustmentModal = () => {
    setIsCreateAdjustmentModalOpen(false);
    setCreateAdjustmentForm({
      ...defaultCreateAdjustmentFormValues,
      adjustment_type: getDefaultAdjustmentType(hasAccountHistory),
    });
  };

  const onCreateAdjustmentFieldChange =
    (field: keyof CreateAdjustmentFormValues) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setCreateAdjustmentForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleCreateAdjustmentRecord = async () => {
    if (!selectedAccountStrategy || !currentAccountId) return;

    const parsedAmount = Number(createAdjustmentForm.adjustment_amount);
    if (!createAdjustmentForm.adjustment_date || Number.isNaN(parsedAmount))
      return;

    await createAccountHistory({
      account: currentAccountId,
      strategy: selectedAccountStrategy.strategy,
      adjustment_date: createAdjustmentForm.adjustment_date,
      adjustment_type: createAdjustmentForm.adjustment_type,
      adjustment_amount: parsedAmount,
    }).unwrap();

    closeCreateAdjustmentModal();
  };

  const handleRemoveAdjustmentRecord = useCallback(
    (id: number) => {
      deleteAccountHistory({ id });
    },
    [deleteAccountHistory],
  );

  const adjustmentRows = useMemo<AdjustmentRow[]>(
    () =>
      accountHistory.map((item) => ({
        id: item.id,
        adjustment_date: item.adjustment_date,
        adjustment_type: item.adjustment_type as AdjustmentTypeEnum,
        adjustment_amount: item.adjustment_amount,
        unit_balance: item.unit_balance_after_adjustment,
      })),
    [accountHistory],
  );

  const adjustmentColumns = useMemo(
    () => getAdjustmentColumns(handleRemoveAdjustmentRecord),
    [handleRemoveAdjustmentRecord],
  );
  const currentUnitBalanceLabel = adjustmentRows.length
    ? Number(adjustmentRows[0].unit_balance).toFixed(2)
    : null;

  return (
    <Box>
      <Card>
        <Box display={"flex"}>
          <AccountPicker />
          <Box marginLeft={1}>
            <AccountStrategyPicker />
          </Box>
          <Box flexGrow={1} marginLeft={1}>
            <Tooltip title="Remove account from selected strategy">
              <IconButton
                onClick={onRemoveAccountStrategy(selectedAccountStrategy?.id)}
                disabled={!selectedAccountStrategy}
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add new strategy to account">
              <IconButton onClick={openAddModal}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton
            onClick={() => setShowHistory((oldValue) => !oldValue)}
            disabled={!selectedAccountStrategy}
          >
            <Typography>{showHistory ? "Hide" : "Show"} History</Typography>
            <Refresh />
          </IconButton>
        </Box>
      </Card>
      {selectedAccountStrategy ? (
        rows.map((row, index) => {
          return (
            <Card key={row.id}>
              <Box margin={1}>
                <Typography variant="body1" color={"green"}>
                  As of {dayjs().format("MM/DD/YYYY")}
                </Typography>
                <DataGrid hideFooter columns={columns} rows={[rows[index]]} />
                <Typography
                  variant="body2"
                  color={"primary.main"}
                  fontWeight={"bold"}
                  marginY={2}
                >
                  Composite Inclusion/Exclusion Notes
                </Typography>
                <Divider />
                {index == 0 ? (
                  <TextField multiline fullWidth value={row.notes} />
                ) : (
                  <Typography variant="body2" marginY={2}>
                    {row.notes}
                  </Typography>
                )}
                {/* <Typography variant="body2" marginY={2}>
                {row.notes}
              </Typography> */}
              </Box>
            </Card>
          );
        })
      ) : (
        <Card>
          <Stack
            height="100%"
            alignItems="center"
            justifyContent="center"
            sx={{ padding: 2, marginY: 2 }}
          >
            Select account strategy
          </Stack>
        </Card>
      )}
      <Card sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              Accumulated Cashflow:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$2,493</Typography>
          </Grid>
          <Grid item xs={7}>
            <Typography color={"green"}>*Since Inception</Typography>{" "}
          </Grid>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              Current Unrealized G/L:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$12,493</Typography>
          </Grid>
          <Grid item xs={7}></Grid>
          <Grid item xs={3}>
            <Typography fontWeight={"bold"} color={"primary.main"}>
              1 Yr, 2 Yr, Drop Cashflow:
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>$29,492</Typography>
          </Grid>
          <Grid item xs={7}></Grid>
        </Grid>
      </Card>
      <Card sx={{ padding: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Typography variant="h6" color="primary.main">
            {hasAccountHistory ? (
              <>
                Current amount of shares for the account:{" "}
                <Typography component="span" color="text.primary">
                  {currentUnitBalanceLabel}
                </Typography>
              </>
            ) : (
              "Please set Inception date and amount"
            )}
          </Typography>
          <Button
            variant="contained"
            onClick={openCreateAdjustmentModal}
            disabled={!selectedAccountStrategy || !currentAccountId}
          >
            Adjust amount
          </Button>
        </Box>
        <DataGrid
          hideFooter
          columns={adjustmentColumns}
          rows={adjustmentRows}
          loading={isAccountHistoryLoading || isDeletingAccountHistory}
          getRowHeight={() => "auto"}
          sx={strategyStyles.adjustmentDataGrid}
        />
      </Card>
      <Dialog
        open={isCreateAdjustmentModalOpen}
        onClose={closeCreateAdjustmentModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adjust Account Amount</DialogTitle>
        <DialogContent>
          <Stack spacing={2} marginTop={1}>
            <Box>
              <Typography variant="body2" marginBottom={0.5}>
                Date
              </Typography>
              <CustomDatePicker
                format="MM/DD/YYYY"
                portal
                zIndex={1600}
                calendarPosition="bottom-left"
                value={
                  createAdjustmentForm.adjustment_date
                    ? new DateObject({
                        date: createAdjustmentForm.adjustment_date,
                        format: "YYYY-MM-DD",
                      })
                    : undefined
                }
                onChange={(date: DateObject | DateObject[] | null) => {
                  if (!date || Array.isArray(date)) {
                    setCreateAdjustmentForm((prev) => ({
                      ...prev,
                      adjustment_date: "",
                    }));
                    return;
                  }

                  setCreateAdjustmentForm((prev) => ({
                    ...prev,
                    adjustment_date: date.format("YYYY-MM-DD"),
                  }));
                }}
                style={{ height: "40px", width: "100%", fontSize: "16px" }}
                placeholder="Pick date"
                showOtherDays
              />
            </Box>
            <Box>
              <Typography variant="body2" marginBottom={0.5}>
                Adjustment Type
              </Typography>
              <Stack direction="row" spacing={1}>
                {availableAdjustmentTypes.map((adjustmentType) => (
                  <Button
                    key={adjustmentType}
                    type="button"
                    variant={
                      createAdjustmentForm.adjustment_type === adjustmentType
                        ? "contained"
                        : "outlined"
                    }
                    color={
                      adjustmentType === AdjustmentTypeEnum.BUY
                        ? "success"
                        : adjustmentType === AdjustmentTypeEnum.SELL
                          ? "error"
                          : "warning"
                    }
                    onClick={() =>
                      setCreateAdjustmentForm((prev) => ({
                        ...prev,
                        adjustment_type: adjustmentType,
                      }))
                    }
                  >
                    {adjustmentType}
                  </Button>
                ))}
              </Stack>
            </Box>
            <TextField
              label="Adjustment Amount"
              type="number"
              value={createAdjustmentForm.adjustment_amount}
              onChange={onCreateAdjustmentFieldChange("adjustment_amount")}
              inputProps={{ min: "0", step: "1" }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateAdjustmentModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdjustmentRecord}
            variant="contained"
            disabled={
              isCreatingAccountHistory ||
              !createAdjustmentForm.adjustment_date ||
              !createAdjustmentForm.adjustment_amount
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <AddAccountStrategyModal
        isOpened={isOpenedAddModal}
        onClose={onAddModalClose}
        accountId={currentAccountId!}
      />
    </Box>
  );
}
